import heroOnyx from "../assets/hero-onyx.jpg";
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scissors, Heart, Sparkles, Users, Star, ArrowRight } from 'lucide-react';

// Image URLs
const IMAGES = {
  hero: heroOnyx,
  women: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/26a91t3s_Goddess%20Braid.png",
  men: "https://images.unsplash.com/photo-1612214070495-ffc6b69cda4b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  braids1: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/ouszjghc_Beard%20grooming.png",
  braids2: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/4y9mb33z_Braids.png",
  natural1: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/32ujcf34_Knotless%20braid.png",
  natural2: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/yko94aez_Knotless%20braids.png",
  fade1: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/wu3ozl2f_Skin%20fade.png",
  fade2: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/6awovhei_Temp%20Fade%20and%20Sponged%20Hair.png",
  locs: "https://customer-assets.emergentagent.com/job_unathi-hair-studio/artifacts/26a91t3s_Goddess%20Braid.png",
  salon: "https://images.pexels.com/photos/7195799/pexels-photo-7195799.jpeg?auto=compress&cs=tinysrgb&w=800",
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

// Women's Services
const womenServices = [
  { name: 'Knotless Braids', price: 'From £120', desc: 'Lightweight, natural-looking braids with minimal tension' },
  { name: 'Box Braids', price: 'From £100', desc: 'Classic protective style with versatile styling options' },
  { name: 'Cornrows', price: 'From £60', desc: 'Traditional African styling with intricate patterns' },
  { name: 'Wig Installation', price: 'From £80', desc: 'Seamless wig fitting and styling' },
  { name: 'Natural Hair Styling', price: 'From £50', desc: 'Embrace and enhance your natural texture' },
  { name: 'Silk Press', price: 'From £65', desc: 'Sleek, straightened look without damage' },
];

// Men's Services
const menServices = [
  { name: 'Haircut / Fade', price: 'From £25', desc: 'Precision cuts and fades tailored to you' },
  { name: 'Beard Grooming', price: 'From £15', desc: 'Shape, trim, and maintain your beard' },
  { name: 'Haircut & Beard Combo', price: 'From £35', desc: 'Complete grooming package' },
  { name: 'Cornrows', price: 'From £50', desc: 'Classic cornrow styles for men' },
  { name: 'Twists', price: 'From £45', desc: 'Two-strand or three-strand twists' },
  { name: 'Dreadlocks Maintenance', price: 'From £60', desc: 'Retwisting, styling, and loc care' },
];

// Why Choose Us
const features = [
  {
    icon: Scissors,
    title: 'Specialists in Black & African Hair',
    desc: 'We understand textured hair, protective styles, scalp care, and healthy hair maintenance.'
  },
  {
    icon: Users,
    title: 'Unisex Salon Experience',
    desc: 'Professional services for both women and men in a stylish and welcoming environment.'
  },
  {
    icon: Sparkles,
    title: 'Luxury & Comfort',
    desc: 'A clean, relaxing, and premium salon experience from start to finish.'
  },
  {
    icon: Heart,
    title: 'Skilled Styling Team',
    desc: 'Dedicated professionals focused on beauty, precision, and client satisfaction.'
  },
];

// Signature Styles
const signatureStyles = [
  'Braids', 'Cornrows', 'Knotless Braids', 'Wigs', 'Natural Hair', 'Silk Press',
  'Dreadlocks', 'Twists', 'Barber Cuts', 'Beard Grooming', 'Kids Styles', 'Occasion Styling'
];

// Testimonials
const testimonials = [
  { text: "Finally, a salon that truly understands our hair.", author: "Amara T." },
  { text: "Professional, warm, and the results were beautiful.", author: "Michelle K." },
  { text: "My braids were neat, light, and exactly what I wanted.", author: "Keisha W." },
  { text: "Best haircut and grooming experience I've had in a long time.", author: "David M." },
];

export const Home = () => {
  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="hero-overlay" />
        
        <div className="container-custom relative z-10 pt-24">
          <div className="max-w-3xl">
            <motion.div 
              className="mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-[#F5F5F5] uppercase tracking-[0.3em] text-sm mb-2">Welcome to</p>
              <h2 className="font-cursive text-[#C9A227] text-7xl md:text-9xl lg:text-[10rem]">Unathi</h2>
            </motion.div>
            
            <motion.h1 
              className="font-heading text-5xl md:text-7xl lg:text-8xl text-[#F5F5F5] font-light leading-none mb-8"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Luxury Hair.<br />
              <span className="italic">Bold Identity.</span><br />
              Timeless Style.
            </motion.h1>
            
            <motion.p 
              className="text-[#999] text-lg md:text-xl max-w-xl mb-10 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              Welcome to UNATHI by Onyx Hairport — a premium unisex salon specialising 
              in Black and African hair, beauty, and grooming for women and men.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <Link to="/book" data-testid="hero-book-btn" className="btn-primary text-center">
                Book Appointment
              </Link>
              <Link to="/services" data-testid="hero-services-btn" className="btn-secondary text-center">
                View Services
              </Link>
            </motion.div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <div className="w-[1px] h-16 bg-gradient-to-b from-[#C9A227] to-transparent" />
        </motion.div>
      </section>

      {/* Introduction Section */}
      <section className="section-dark py-24 md:py-32" data-testid="intro-section">
        <div className="container-custom">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.div 
              className="w-16 h-[1px] bg-[#C9A227] mx-auto mb-8"
              variants={fadeInUp}
            />
            <motion.p 
              className="text-[#F5F5F5] text-lg md:text-xl leading-relaxed mb-10"
              variants={fadeInUp}
            >
              UNATHI by Onyx Hairport is a premium unisex salon created for clients who value 
              style, healthy hair, professionalism, and cultural understanding. We specialise in 
              Black and African hair care, protective styling, natural hair, braiding, barbering, 
              grooming, and beauty services for both women and men.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link 
                to="/about" 
                data-testid="intro-learn-more-btn"
                className="inline-flex items-center gap-2 text-[#C9A227] uppercase tracking-widest text-sm hover:gap-4 transition-all"
              >
                Learn More <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Services Section */}
      <section className="py-24 md:py-32 bg-[#F9F9F9]" data-testid="services-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.p 
              className="text-[#C9A227] uppercase tracking-[0.3em] text-sm mb-4"
              variants={fadeInUp}
            >
              What We Offer
            </motion.p>
            <motion.h2 
              className="font-heading text-4xl md:text-5xl text-[#2E2E2E]"
              variants={fadeInUp}
            >
              Our <span className="italic">Services</span>
            </motion.h2>
          </motion.div>

          {/* Women's Services */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-[1px] flex-1 bg-[#C9A227]/30" />
              <h3 className="font-heading text-2xl text-[#2E2E2E]">Women's Hair Services</h3>
              <div className="h-[1px] flex-1 bg-[#C9A227]/30" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {womenServices.map((service, index) => (
                <motion.div
                  key={service.name}
                  className="bg-white p-6 border border-neutral-100 hover:border-[#C9A227]/50 hover:shadow-lg transition-all group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`service-card-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-heading text-xl text-[#2E2E2E] group-hover:text-[#C9A227] transition-colors">
                      {service.name}
                    </h4>
                    <span className="price-tag text-sm">{service.price}</span>
                  </div>
                  <p className="text-[#666] text-sm mb-4">{service.desc}</p>
                  <Link 
                    to="/book" 
                    className="text-[#C9A227] text-sm uppercase tracking-wider hover:underline"
                  >
                    Book Now
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Men's Services */}
          <div>
            <div className="flex items-center gap-4 mb-8">
              <div className="h-[1px] flex-1 bg-[#C9A227]/30" />
              <h3 className="font-heading text-2xl text-[#2E2E2E]">Men's Grooming Services</h3>
              <div className="h-[1px] flex-1 bg-[#C9A227]/30" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {menServices.map((service, index) => (
                <motion.div
                  key={service.name}
                  className="bg-white p-6 border border-neutral-100 hover:border-[#C9A227]/50 hover:shadow-lg transition-all group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`service-card-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-heading text-xl text-[#2E2E2E] group-hover:text-[#C9A227] transition-colors">
                      {service.name}
                    </h4>
                    <span className="price-tag text-sm">{service.price}</span>
                  </div>
                  <p className="text-[#666] text-sm mb-4">{service.desc}</p>
                  <Link 
                    to="/book" 
                    className="text-[#C9A227] text-sm uppercase tracking-wider hover:underline"
                  >
                    Book Now
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/services" data-testid="view-all-services-btn" className="btn-primary bg-[#2E2E2E] hover:bg-[#C9A227]">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-dark py-24 md:py-32" data-testid="why-us-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.p 
              className="text-[#C9A227] uppercase tracking-[0.3em] text-sm mb-4"
              variants={fadeInUp}
            >
              The UNATHI Difference
            </motion.p>
            <motion.h2 
              className="font-heading text-4xl md:text-5xl text-[#F5F5F5]"
              variants={fadeInUp}
            >
              Why Choose <span className="italic">Us</span>
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                data-testid={`feature-${index}`}
              >
                <div className="feature-icon mx-auto">
                  <feature.icon size={24} />
                </div>
                <h4 className="font-heading text-xl text-[#F5F5F5] mb-3">{feature.title}</h4>
                <p className="text-[#999] text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Signature Styles Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="styles-section">
        <div className="container-custom">
          <motion.div 
            className="grid lg:grid-cols-2 gap-16 items-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <div>
              <motion.p 
                className="text-[#C9A227] uppercase tracking-[0.3em] text-sm mb-4"
                variants={fadeInUp}
              >
                Our Specialty
              </motion.p>
              <motion.h2 
                className="font-heading text-4xl md:text-5xl text-[#F5F5F5] mb-6"
                variants={fadeInUp}
              >
                Signature <span className="italic">Styles</span>
              </motion.h2>
              <motion.p 
                className="text-[#999] leading-relaxed mb-8"
                variants={fadeInUp}
              >
                We celebrate the beauty, versatility, and creativity of Black and African hair 
                through styles that protect, enhance, and express identity.
              </motion.p>
              
              <motion.div 
                className="flex flex-wrap gap-3"
                variants={fadeInUp}
              >
                {signatureStyles.map((style) => (
                  <span 
                    key={style}
                    className="px-4 py-2 border border-[#C9A227]/30 text-[#F5F5F5] text-sm hover:border-[#C9A227] hover:bg-[#C9A227]/10 transition-all cursor-default"
                  >
                    {style}
                  </span>
                ))}
              </motion.div>
            </div>

            {/* Images Grid */}
            <motion.div 
              className="grid grid-cols-2 gap-4"
              variants={fadeInUp}
            >
              <div className="space-y-4">
                <div className="img-container aspect-[3/4] rounded-none overflow-hidden">
                  <img src={IMAGES.braids1} alt="Braids hairstyle" className="w-full h-full object-cover" />
                </div>
                <div className="img-container aspect-square rounded-none overflow-hidden">
                  <img src={IMAGES.fade1} alt="Fade haircut" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="img-container aspect-square rounded-none overflow-hidden">
                  <img src={IMAGES.natural1} alt="Natural hair" className="w-full h-full object-cover" />
                </div>
                <div className="img-container aspect-[3/4] rounded-none overflow-hidden">
                  <img src={IMAGES.locs} alt="Locs hairstyle" className="w-full h-full object-cover" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Preview Section */}
      <section className="py-24 md:py-32 bg-[#F9F9F9]" data-testid="gallery-preview-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.p 
              className="text-[#C9A227] uppercase tracking-[0.3em] text-sm mb-4"
              variants={fadeInUp}
            >
              Style Inspiration
            </motion.p>
            <motion.h2 
              className="font-heading text-4xl md:text-5xl text-[#2E2E2E]"
              variants={fadeInUp}
            >
              Our <span className="italic">Gallery</span>
            </motion.h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            {[
              { src: IMAGES.braids2, alt: "Braids" },
              { src: IMAGES.natural2, alt: "Natural hair" },
              { src: IMAGES.fade2, alt: "Fade haircut" },
              { src: IMAGES.salon, alt: "Salon interior" },
              { src: IMAGES.women, alt: "Women's styling" },
              { src: IMAGES.men, alt: "Men's grooming" },
              { src: IMAGES.braids1, alt: "Knotless braids" },
              { src: IMAGES.locs, alt: "Locs styling" },
            ].map((img, index) => (
              <motion.div
                key={index}
                className={`img-container overflow-hidden ${index === 0 || index === 7 ? 'md:col-span-2 md:row-span-2' : ''}`}
                style={{ aspectRatio: index === 0 || index === 7 ? '1' : '1' }}
                variants={fadeInUp}
              >
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-12">
            <Link to="/gallery" data-testid="view-gallery-btn" className="btn-primary bg-[#2E2E2E] hover:bg-[#C9A227]">
              View Full Gallery
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-dark py-24 md:py-32" data-testid="testimonials-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.p 
              className="text-[#C9A227] uppercase tracking-[0.3em] text-sm mb-4"
              variants={fadeInUp}
            >
              Client Love
            </motion.p>
            <motion.h2 
              className="font-heading text-4xl md:text-5xl text-[#F5F5F5]"
              variants={fadeInUp}
            >
              What They <span className="italic">Say</span>
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="text-[#C9A227] fill-[#C9A227]" />
                  ))}
                </div>
                <p className="text-[#F5F5F5] font-accent italic mb-4">"{testimonial.text}"</p>
                <p className="text-[#C9A227] text-sm">— {testimonial.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-24 md:py-32 relative"
        style={{ 
          backgroundImage: `url(${IMAGES.salon})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-black/80" />
        <div className="container-custom relative z-10">
          <motion.div 
            className="max-w-2xl mx-auto text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 
              className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#F5F5F5] mb-6"
              variants={fadeInUp}
            >
              Book Your Next Look with <span className="text-[#C9A227] italic">UNATHI</span>
            </motion.h2>
            <motion.p 
              className="text-[#999] text-lg mb-10"
              variants={fadeInUp}
            >
              Whether you want braids, natural styling, wig installation, loc maintenance, 
              or a fresh cut, our team is ready to help you look and feel your best.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/book" data-testid="cta-book-btn" className="btn-primary">
                Book Appointment
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Home;
